"use client";

/**
 * Pre-order a pressing from its marketplace page (Gordon 2026-11-05: "put it
 * in the marketplace for pre-release, pre-purchase"). The pressing's price is
 * the floor and there is no ceiling ("minimum 349 but no maximum"); buyers who
 * want to can be named in the pressing's Hall of Fame, and those who pay more
 * than the price also on the Angels list. Shipping is priced for the chosen
 * country (ShipEngine's live rate from Pitch Beats when set up, else the
 * pressing's zone price). Pays through /api/checkout type 'physical-media'.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { CalendarClock, CheckCircle2, Crown, Disc3, Factory, Loader2, Play } from "lucide-react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useMediaPlayer } from "@/contexts/media-player-context";
import { ParentalAdvisoryLabel } from "@/components/ui/parental-advisory-label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/auth-context";

interface PublicPressing {
  id: string; status: string; providerSlug: string; currency: string; basePriceCents: number; payWhatYouFeel: boolean;
  leadTimeWeeks: number | null; shipsEstimate: string; liveShipping: boolean; shipCountries: string[]; releaseId: string; explicit: boolean;
  variants: { id: string; label: string; priceCents: number | null; soldOut: boolean }[];
}
interface ShipQuote { amountCents: number; source: "shipengine" | "zone"; service?: string; days?: number | null }

export function PressingPreorder({ pressingId }: { pressingId: string }) {
  const t = useTranslations("marketplace.preorder");
  const locale = useLocale();
  const { user, userProfile } = useAuth() as any;
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  const [p, setP] = useState<PublicPressing | null>(null);
  const [hall, setHall] = useState<{ name: string; paidCents: number }[]>([]);
  const [failed, setFailed] = useState(false);
  const [variantId, setVariantId] = useState("");
  const [country, setCountry] = useState("");
  const [postal, setPostal] = useState("");
  const [ship, setShip] = useState<ShipQuote | null>(null);
  const [shipState, setShipState] = useState<"idle" | "loading" | "error">("idle");
  const [amount, setAmount] = useState("");
  const [named, setNamed] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [adult, setAdult] = useState(false);
  const { playTracks } = useMediaPlayer();
  const [playing, setPlaying] = useState(false);

  /** Play the album that's already on BB (Gordon: "a play release button that is online already").
   *  Each track's own work comes first — a release's track list is a copy that can be stale. */
  const playRelease = async () => {
    if (!p?.releaseId) return;
    setPlaying(true);
    try {
      const rel = (await getDoc(doc(db, "releases", p.releaseId))).data() as any;
      if (!rel) return;
      const list: any[] = Array.isArray(rel.tracks) ? rel.tracks : [];
      const works = await Promise.all(list.map(t => (t?.workId || t?.id) ? getDoc(doc(db, "works", String(t.workId || t.id))).then(d => d.data() as any).catch(() => null) : Promise.resolve(null)));
      const tracks = list.map((t, i) => {
        const w = works[i];
        if (w?.adminHidden) return null;
        const audioUrl = w?.audioUrl || w?.mp3Url || t?.mp3Url || t?.audioUrl;
        if (!audioUrl) return null;
        return {
          id: String(t.workId || t.id || `${p.releaseId}-${i}`), title: String(w?.title || t?.title || ""),
          artist: String(rel.artistName || rel.artist?.name || ""), imageUrl: String(rel.coverArtUrl || ""),
          audioUrl: String(audioUrl), mediaType: "audio" as const, explicit: !!(w?.explicit ?? rel.explicit),
        };
      }).filter(Boolean) as any[];
      if (tracks.length) playTracks(tracks, 0);
    } finally { setPlaying(false); }
  };

  useEffect(() => {
    fetch(`/api/pressings/public?id=${encodeURIComponent(pressingId)}`).then(r => r.ok ? r.json() : null).then(d => {
      if (!d?.pressing) { setFailed(true); return; }
      setP(d.pressing);
      setHall(Array.isArray(d.hallOfFame) ? d.hallOfFame : []);
      setVariantId(d.pressing.variants.find((v: any) => !v.soldOut)?.id || "");
      const list: string[] = d.pressing.shipCountries || [];
      const mine = (navigator.language.split("-")[1] || "").toUpperCase();
      setCountry(list.includes(mine) ? mine : list.includes("SE") ? "SE" : list[0] || "");
    }).catch(() => setFailed(true));
  }, [pressingId]);
  useEffect(() => { if (!name) setName(userProfile?.displayName || user?.displayName || ""); }, [user, userProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Shipping for the chosen country (and postcode when the price is live), newest answer wins.
  const seq = useRef(0);
  useEffect(() => {
    if (!p || !country) return;
    const n = ++seq.current;
    setShipState("loading");
    const timer = setTimeout(async () => {
      try {
        const res = await fetch("/api/shipping/quote", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pressingId: p.id, country, postalCode: postal }) });
        const d = await res.json().catch(() => null);
        if (n !== seq.current) return;
        if (res.ok && d?.quote) { setShip(d.quote); setShipState("idle"); } else { setShip(null); setShipState("error"); }
      } catch { if (n === seq.current) { setShip(null); setShipState("error"); } }
    }, 400);
    return () => clearTimeout(timer);
  }, [p, country, postal]);

  const variant = p?.variants.find(v => v.id === variantId);
  const floor = (variant?.priceCents ?? p?.basePriceCents ?? 0) / 100;
  const pay = Math.min(999_999, Math.max(floor, Number(amount.replace(",", ".")) || floor));
  const cur = (p?.currency || "SEK").toUpperCase();
  const fmt = useMemo(() => new Intl.NumberFormat(locale, { style: "currency", currency: cur, maximumFractionDigits: cur === "SEK" ? 0 : 2 }), [locale, cur]);
  const regionName = useMemo(() => { try { return new Intl.DisplayNames([locale], { type: "region" }); } catch { return null; } }, [locale]);
  const countries = useMemo(() => (p?.shipCountries || []).map(c => ({ code: c, name: regionName?.of(c) || c })).sort((a, b) => a.name.localeCompare(b.name, locale)), [p, regionName, locale]);
  const estimate = useMemo(() => {
    if (!p?.shipsEstimate || !/^\d{4}-\d{2}$/.test(p.shipsEstimate)) return "";
    const [y, m] = p.shipsEstimate.split("-").map(Number);
    return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(new Date(y, m - 1, 15));
  }, [p?.shipsEstimate, locale]);

  if (failed) return null;
  if (!p) return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /></div>;

  const done = params.get("preordered") === "1";
  const preorder = async () => {
    if (!user) { router.push(`/signin?redirect=${encodeURIComponent(pathname)}`); return; }
    setBusy(true); setError("");
    try {
      const res = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${await user.getIdToken()}` },
        body: JSON.stringify({
          type: "physical-media", pressingId: p.id, variantId: variantId || undefined, quantity: 1,
          amountCents: Math.round(pay * 100), shipCountry: country || undefined, shipPostal: postal || undefined,
          ...(named && name.trim() ? { angel: true, angelName: name.trim() } : {}),
          ...(p.explicit ? { ageConfirmed: adult } : {}),
          successUrl: `${window.location.origin}${pathname}?preordered=1`, cancelUrl: window.location.href,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.url) throw new Error(data?.error || String(res.status));
      window.location.href = data.url;
    } catch { setError(t("error")); setBusy(false); }
  };

  return (
    <div className="space-y-3 rounded-lg border-2 border-foreground/80 p-4">
      {done && (
        <p className="flex items-start gap-2 rounded-md bg-primary p-3 text-sm font-medium text-primary-foreground" role="status">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />{t("thanks")}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded bg-primary px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-primary-foreground">{t("badge")}</span>
        {p.providerSlug === "pitchbeats" && (
          <a href="https://pitchbeats.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground hover:underline">
            <Factory className="h-3.5 w-3.5" aria-hidden="true" />{t("madeByPitchbeats")}
          </a>
        )}
      </div>
      {p.releaseId && (
        <Button variant="outlineContrast" className="w-full gap-2" onClick={playRelease} disabled={playing}>
          {playing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}{t("playAlbum")}
        </Button>
      )}
      {estimate && <p className="flex items-center gap-2 text-sm"><CalendarClock className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />{t("ships", { when: estimate })}</p>}
      {p.status === "crowdfunding" && <p className="text-sm text-muted-foreground">{t("pressedWhen")}</p>}

      {p.variants.length > 1 && (
        <div className="space-y-1">
          <Label htmlFor="po-variant" className="text-xs font-semibold">{t("version")}</Label>
          <select id="po-variant" className="h-9 w-full rounded-md border-2 border-input bg-background px-2 text-sm" value={variantId} onChange={e => setVariantId(e.target.value)}>
            {p.variants.map(v => <option key={v.id} value={v.id} disabled={v.soldOut}>{v.label}</option>)}
          </select>
        </div>
      )}
      {p.variants.length === 1 && variant?.label && <p className="flex items-center gap-2 text-sm font-medium"><Disc3 className="h-4 w-4 shrink-0" aria-hidden="true" />{variant.label}</p>}

      {countries.length > 0 && (
        <div className="grid grid-cols-[1fr_auto] gap-2">
          <div className="space-y-1">
            <Label htmlFor="po-country" className="text-xs font-semibold">{t("shipTo")}</Label>
            <select id="po-country" className="h-9 w-full rounded-md border-2 border-input bg-background px-2 text-sm" value={country} onChange={e => setCountry(e.target.value)}>
              {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
          {p.liveShipping && (
            <div className="w-28 space-y-1">
              <Label htmlFor="po-postal" className="text-xs font-semibold">{t("postalCode")}</Label>
              <Input id="po-postal" className="h-9 px-2" value={postal} autoComplete="postal-code" onChange={e => setPostal(e.target.value.slice(0, 12))} />
            </div>
          )}
        </div>
      )}

      {p.payWhatYouFeel && (
        <div className="space-y-1">
          <Label htmlFor="po-amount" className="text-xs font-semibold">{t("yourPrice", { min: fmt.format(floor) })}</Label>
          <Input id="po-amount" inputMode="decimal" className="h-9 px-2" placeholder={String(floor)} value={amount}
            onChange={e => setAmount(e.target.value.replace(/[^\d.,]/g, ""))} />
          <p className="text-2xs text-muted-foreground">{t("payMoreHint")}</p>
        </div>
      )}

      <div className="space-y-2 rounded-md border p-3">
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-0.5 h-4 w-4" checked={named} onChange={e => setNamed(e.target.checked)} />
          <span>{t("nameOptIn")}</span>
        </label>
        {named && (
          <div className="space-y-1">
            <Label htmlFor="po-name" className="text-xs font-semibold">{t("nameShown")}</Label>
            <Input id="po-name" className="h-9 px-2" maxLength={60} value={name} onChange={e => setName(e.target.value)} />
          </div>
        )}
      </div>

      {p.explicit && (
        <div className="flex items-start gap-3 rounded-md border-2 border-foreground/60 p-3">
          <ParentalAdvisoryLabel width={56} className="shrink-0" />
          <div className="space-y-1.5 text-sm">
            <p className="font-semibold">{t("ageTitle")}</p>
            <p className="text-muted-foreground">{t("ageBody")}</p>
            <label className="flex items-center gap-2 font-medium">
              <input type="checkbox" className="h-4 w-4" checked={adult} onChange={e => setAdult(e.target.checked)} />
              {t("ageConfirm")}
            </label>
          </div>
        </div>
      )}

      <dl className="space-y-1 text-sm">
        <div className="flex justify-between"><dt>{t("record")}</dt><dd className="tabular-nums">{fmt.format(pay)}</dd></div>
        <div className="flex justify-between gap-2">
          <dt>{t("shipping")}{ship?.source === "shipengine" && ship.service ? <span className="text-muted-foreground"> · {ship.service}</span> : null}</dt>
          <dd className="tabular-nums">{shipState === "loading" ? <Loader2 className="inline h-3.5 w-3.5 animate-spin" /> : ship ? fmt.format(ship.amountCents / 100) : "—"}</dd>
        </div>
        <div className="flex justify-between border-t pt-1 font-bold"><dt>{t("total")}</dt><dd className="tabular-nums">{fmt.format(pay + (ship ? ship.amountCents / 100 : 0))}</dd></div>
      </dl>
      {shipState === "error" && <p className="text-sm text-destructive">{t("shippingUnavailable")}</p>}
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      <Button size="lg" className="w-full gap-2 text-base" onClick={preorder} disabled={busy || (!variant && p.variants.length > 0) || (countries.length > 0 && !ship) || (p.explicit && !adult)}>
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Disc3 className="h-5 w-5" />}
        {user ? t("cta") : t("signInCta")}
      </Button>
      <p className="text-2xs text-muted-foreground">{t("refundNote")}</p>

      {hall.length > 0 && (
        <div className="space-y-1.5 border-t pt-3">
          <h3 className="flex items-center gap-1.5 text-sm font-bold"><Crown className="h-4 w-4 text-primary" aria-hidden="true" />{t("hallOfFame")}</h3>
          <ol className="space-y-1 text-sm">
            {hall.map((h, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="w-5 shrink-0 text-right font-bold tabular-nums">{i + 1}</span>
                <span className="min-w-0 flex-1 truncate">{h.name}</span>
                <span className="tabular-nums text-muted-foreground">{fmt.format(h.paidCents / 100)}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
